/**
 * Security Wrapper Component for EASI 2.0
 *
 * Provides application-wide security features:
 * - Session timeout monitoring with warnings
 * - HTTPS validation in production
 * - Automatic logout on inactivity
 * - Security context for child components
 *
 * HIPAA Requirements:
 * - 164.312(a)(2)(iii): Automatic logoff
 * - 164.312(e)(1): Transmission security
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import {
  initializePHIProtection,
  clearSensitiveData,
  createSessionTracker,
  validateSecureConnection,
} from '../security/phi-protection';
import { useAuth } from '../hooks/useAuth';
import { config } from '../config/environment';
import { autoSaveRegistry, useBeforeUnload } from '../hooks/useAutoSave';

/**
 * Security context state
 */
interface SecurityState {
  isSecure: boolean;
  sessionTimeRemaining: number;
  showTimeoutWarning: boolean;
}

/**
 * Security context actions
 */
interface SecurityActions {
  extendSession: () => void;
  dismissWarning: () => void;
  forceLogout: () => void;
}

/**
 * Combined security context type
 */
type SecurityContextType = SecurityState & SecurityActions;

const SecurityContext = createContext<SecurityContextType | undefined>(undefined);

/**
 * Security Wrapper Props
 */
interface SecurityWrapperProps {
  children: ReactNode;
}

/**
 * Session Timeout Warning Dialog
 */
function TimeoutWarningDialog({
  timeRemaining,
  onExtend,
  onLogout,
}: {
  timeRemaining: number;
  onExtend: () => void;
  onLogout: () => void;
}) {
  const minutes = Math.floor(timeRemaining / 60000);
  const seconds = Math.floor((timeRemaining % 60000) / 1000);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      role="dialog"
      aria-labelledby="timeout-title"
      aria-describedby="timeout-description"
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '400px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        }}
      >
        <h2
          id="timeout-title"
          style={{
            margin: '0 0 16px 0',
            fontSize: '20px',
            color: '#d32f2f',
          }}
        >
          Session Expiring
        </h2>
        <p
          id="timeout-description"
          style={{
            margin: '0 0 24px 0',
            color: '#666',
          }}
        >
          Your session will expire in{' '}
          <strong>
            {minutes}:{seconds.toString().padStart(2, '0')}
          </strong>{' '}
          due to inactivity. Would you like to continue your session?
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onLogout}
            style={{
              padding: '10px 20px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Logout
          </button>
          <button
            onClick={onExtend}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: '#10B981',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Continue Session
          </button>
        </div>
        <p style={{ margin: '16px 0 0 0', color: '#999', fontSize: '12px', textAlign: 'center' }}>
          Having trouble?{' '}
          <a href="mailto:admin@itherapyllc.com" style={{ color: '#10B981' }}>
            admin@itherapyllc.com
          </a>
        </p>
      </div>
    </div>
  );
}

/**
 * Insecure Connection Warning
 */
function InsecureConnectionWarning() {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
      }}
    >
      <div
        style={{
          textAlign: 'center',
          padding: '40px',
          maxWidth: '500px',
        }}
      >
        <div
          style={{
            marginBottom: '20px',
          }}
        >
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h1.9c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm0 12H6V10h12v10z" fill="#d32f2f"/>
          </svg>
        </div>
        <h1
          style={{
            margin: '0 0 16px 0',
            color: '#d32f2f',
          }}
        >
          Insecure Connection
        </h1>
        <p style={{ color: '#666', marginBottom: '24px' }}>
          This application requires a secure HTTPS connection to protect patient data.
          Please access the application using HTTPS.
        </p>
        <p style={{ color: '#999', fontSize: '14px' }}>
          Current URL: {typeof window !== 'undefined' ? window.location.href : ''}
        </p>
      </div>
    </div>
  );
}

/**
 * Security Wrapper Component
 *
 * Wraps the application to provide security features.
 */
export function SecurityWrapper({ children }: SecurityWrapperProps) {
  const { isAuthenticated, signOut } = useAuth();
  const [isSecure, setIsSecure] = useState(true);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(
    config.security.sessionTimeoutMs
  );
  const trackerRef = useRef<ReturnType<typeof createSessionTracker> | null>(null);

  // Global beforeunload warning for unsaved changes
  useBeforeUnload();

  // Initialize PHI protection on mount
  useEffect(() => {
    initializePHIProtection();
  }, []);

  // Validate secure connection
  useEffect(() => {
    const secure = validateSecureConnection();
    setIsSecure(secure);
  }, []);

  // Session timeout tracking — HIPAA §164.312(a)(2)(iii)
  // IMPORTANT: showTimeoutWarning is NOT in the dependency array.
  // Including it caused the tracker to be recreated when the warning showed,
  // resetting lastActivity and preventing the actual timeout from ever firing.
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Always use the HIPAA-compliant timeout (7 min) regardless of "Remember me".
    // "Remember me" only pre-fills the email on the login form.
    const tracker = createSessionTracker();
    trackerRef.current = tracker;

    const handleTimeout = () => {
      // Save current location BEFORE clearing anything — user returns here after re-login.
      // Use localStorage (not sessionStorage) because we clear sessionStorage below.
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath && currentPath !== '/' && currentPath !== '/login') {
        localStorage.setItem('easi_redirect_after_login', currentPath);
      }

      // Flush all auto-saves before clearing data (fire-and-forget, max 3s)
      autoSaveRegistry.flushAll().finally(() => {
        // Clear ALL sensitive data and tokens
        clearSensitiveData();
        localStorage.removeItem('easi_id_token');
        localStorage.removeItem('easi_access_token');
        localStorage.removeItem('easi_refresh_token');
        localStorage.removeItem('easi_last_activity');
        localStorage.removeItem('easi_remember_me');
        // Clear only auth-related sessionStorage — NEVER destroy workflow state.
        // User work lives in DynamoDB and must survive session expiration.
        sessionStorage.removeItem('easi_oauth_error');
        sessionStorage.removeItem('easi_agreement_status');

        // Revoke Cognito session (fire-and-forget — don't let failures block redirect)
        signOut().catch(() => {});

        // Hard redirect to login — bypasses all React state race conditions
        window.location.href = '/login?session_expired=true';
      });
    };

    const handleWarning = () => {
      setShowTimeoutWarning(true);
    };

    tracker.start(handleTimeout, handleWarning);

    // ── Last-activity heartbeat ─────────────────────────────────────
    // Persist a timestamp to localStorage on every user interaction so
    // checkAuth() can enforce the 7-minute inactivity timeout even
    // after the browser is closed and reopened (localStorage survives
    // "clear cache and history" in all browsers).
    // Throttled to max once per 10 seconds to avoid excessive I/O.
    const HEARTBEAT_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const;
    let lastHeartbeatWrite = 0;
    const writeActivityHeartbeat = () => {
      const now = Date.now();
      if (now - lastHeartbeatWrite > 10_000) {
        localStorage.setItem('easi_last_activity', now.toString());
        lastHeartbeatWrite = now;
      }
    };
    // Seed immediately so the timestamp exists from session start
    localStorage.setItem('easi_last_activity', Date.now().toString());
    lastHeartbeatWrite = Date.now();
    for (const event of HEARTBEAT_EVENTS) {
      document.addEventListener(event, writeActivityHeartbeat, { passive: true });
    }

    // ── API activity reset ──────────────────────────────────────────────
    // Active API calls (polling, analysis, report generation) mean the
    // system is working on the clinician's behalf. They are NOT idle.
    // Dispatched by axios interceptors in client.ts.
    const handleApiActivity = () => {
      writeActivityHeartbeat();
      tracker.recordApiActivity();
      setShowTimeoutWarning(false);
      setSessionTimeRemaining(config.security.sessionTimeoutMs);
    };
    window.addEventListener('easi-api-activity', handleApiActivity);

    // ── Tab-focus activity reset ──────────────────────────────────────
    // DOM events (mouse, keyboard, touch) do NOT fire when the tab is
    // backgrounded. Without this listener, a clinician who reads a
    // transcript for 5 min then checks email for 2 min = 7 min
    // "inactive" and gets logged out. Returning to the tab IS activity.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        localStorage.setItem('easi_last_activity', now.toString());
        lastHeartbeatWrite = now;
        // Reset the in-memory tracker so the 7-min countdown restarts
        tracker.resetSession();
        // Dismiss any timeout warning that appeared while tab was hidden
        setShowTimeoutWarning(false);
        setSessionTimeRemaining(config.security.sessionTimeoutMs);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Update remaining time every second (reads from the SAME tracker instance)
    const intervalId = setInterval(() => {
      const remaining = tracker.getRemainingTime();
      setSessionTimeRemaining(remaining);
    }, 1000);

    return () => {
      tracker.stop();
      trackerRef.current = null;
      clearInterval(intervalId);
      for (const event of HEARTBEAT_EVENTS) {
        document.removeEventListener(event, writeActivityHeartbeat);
      }
      window.removeEventListener('easi-api-activity', handleApiActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, signOut]);

  /**
   * Extend session by recording activity
   */
  const extendSession = useCallback(() => {
    setShowTimeoutWarning(false);
    setSessionTimeRemaining(config.security.sessionTimeoutMs);
    // Explicitly reset the tracker's internal state (lastActivity + warningShown)
    trackerRef.current?.resetSession();
  }, []);

  /**
   * Dismiss warning without extending
   */
  const dismissWarning = useCallback(() => {
    setShowTimeoutWarning(false);
  }, []);

  /**
   * Force immediate logout
   */
  const forceLogout = useCallback(() => {
    setShowTimeoutWarning(false);
    // Save current location for post-login redirect
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath && currentPath !== '/' && currentPath !== '/login') {
      localStorage.setItem('easi_redirect_after_login', currentPath);
    }
    // Flush all pending auto-saves before destroying session (same as handleTimeout)
    autoSaveRegistry.flushAll().finally(() => {
      clearSensitiveData();
      localStorage.removeItem('easi_id_token');
      localStorage.removeItem('easi_access_token');
      localStorage.removeItem('easi_refresh_token');
      localStorage.removeItem('easi_last_activity');
      localStorage.removeItem('easi_remember_me');
      // Clear only auth-related sessionStorage — NEVER destroy workflow state
      sessionStorage.removeItem('easi_oauth_error');
      sessionStorage.removeItem('easi_agreement_status');
      signOut().catch(() => {});
      window.location.href = '/login?session_expired=true';
    });
  }, [signOut]);

  /**
   * Context value
   */
  const contextValue = useMemo<SecurityContextType>(
    () => ({
      isSecure,
      sessionTimeRemaining,
      showTimeoutWarning,
      extendSession,
      dismissWarning,
      forceLogout,
    }),
    [
      isSecure,
      sessionTimeRemaining,
      showTimeoutWarning,
      extendSession,
      dismissWarning,
      forceLogout,
    ]
  );

  // Block insecure connections in production
  if (!isSecure) {
    return <InsecureConnectionWarning />;
  }

  return (
    <SecurityContext.Provider value={contextValue}>
      {children}
      {showTimeoutWarning && isAuthenticated && (
        <TimeoutWarningDialog
          timeRemaining={sessionTimeRemaining}
          onExtend={extendSession}
          onLogout={forceLogout}
        />
      )}
    </SecurityContext.Provider>
  );
}

/**
 * Hook to access security context
 */
export function useSecurity(): SecurityContextType {
  const context = useContext(SecurityContext);

  if (context === undefined) {
    throw new Error('useSecurity must be used within a SecurityWrapper');
  }

  return context;
}

/**
 * Higher-order component for security-sensitive components
 */
export function withSecurity<P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> {
  return function SecurityWrappedComponent(props: P) {
    const security = useSecurity();

    // Don't render if connection is insecure
    if (!security.isSecure) {
      return null;
    }

    return <Component {...props} />;
  };
}

export default SecurityWrapper;
